import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import UniversalIconBtn from '../../../shared/component/headless/button/UniversalIconBtn';
import HeadlessBtn from '../../../shared/component/headless/button/HeadlessBtn';
import { BlackFullWidthBtn } from '../../../shared/component/headless/button/BtnBody';
import { getBtnName } from '../../../shared/constants/btnNames';
import { getModalMessage } from '../../../shared/constants/modalMessages';
import { useGroupMemberTagStore } from '../../../shared/store/groupMemberTagStore';
import { useModalStore } from '../../../shared/store/portal/modalStore';
import belowIcon from '../../../asset/icon/below.png';
import { usePageOwnerIdentityStore } from '../../../shared/store/pageOwnerIdentityStore';
import { canShowGroupMemberTagList } from '../../../shared/util/visibility/groupMemberVisibility';

const GroupMemberLabelSelectModalMobile: React.FC = () => {
  const { clearModal } = useModalStore();
  const pageOwner = usePageOwnerIdentityStore((s) => s.pageOwner);
  const {
    tags,
    selectedTags,
    listOwnerAui,
    status,
    addSelectedTag,
    removeSelectedTag,
  } = useGroupMemberTagStore();
  const canShowTags = canShowGroupMemberTagList({
    pageOwnerAui: pageOwner?.aui,
    listOwnerAui,
    status,
  });
  const visibleTags = canShowTags ? tags : [];
  const visibleSelectedTags = canShowTags ? selectedTags : [];

  const initialSelectedIdSet = useMemo(
    () => new Set(visibleSelectedTags.map((tag) => tag.id)),
    [visibleSelectedTags],
  );
  const [pendingSelectedIdSet, setPendingSelectedIdSet] = useState<Set<number>>(initialSelectedIdSet);

  const toggleLabel = (labelId: number) => {
    setPendingSelectedIdSet((previous) => {
      const next = new Set(previous);
      if (next.has(labelId)) next.delete(labelId);
      else next.add(labelId);
      return next;
    });
  };

  const handleApply = () => {
    visibleTags.forEach((tag) => {
      const wasSelected = initialSelectedIdSet.has(tag.id);
      const isSelected = pendingSelectedIdSet.has(tag.id);
      if (wasSelected === isSelected) return;
      if (isSelected) addSelectedTag(tag);
      else removeSelectedTag(tag);
    });
    clearModal(400);
  };

  const handleClose = () => {
    clearModal(400);
  };

  return (
    <Container>
      <TitleWrapper>
        <Title>{getModalMessage('SELECT_TAG_STATION_TITLE')}</Title>
        <UniversalIconBtn
          size="fit"
          border={false}
          isTransparent={true}
          backgroundImg={belowIcon}
          onClick={handleClose}
          aria-label="Close"
        />
      </TitleWrapper>
      <ContentContainer>
        {visibleTags.map((tag) => {
          const isSelected = pendingSelectedIdSet.has(tag.id);
          return (
            <LabelItem key={tag.id} $isSelected={isSelected} onClick={() => toggleLabel(tag.id)}>
              {tag.name}
            </LabelItem>
          );
        })}
      </ContentContainer>
      <HeadlessBtn value={getBtnName('APPLY')} handleClick={handleApply} StyledBtn={ApplyButton} />
    </Container>
  );
};

export default GroupMemberLabelSelectModalMobile;

const Container = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const TitleWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  color: ${({ theme }) => theme.colors.color_Gray_02};
  margin-top: clamp(12px, 2vh, 30px);
  margin-bottom: clamp(20px, 8vh, 30px);
`;

const Title = styled.h2`
  ${({ theme }) => theme.typography.Body_01_1};
  color: ${({ theme }) => theme.colors.color_Gray_02};
`;


const ContentContainer = styled.div`
  max-height: 45vh;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const LabelItem = styled.div<{ $isSelected: boolean }>`
  width: 100%;
  padding: 4px 0;
  border-bottom: 1px dotted ${({ $isSelected, theme }) => ($isSelected ? theme.colors.color_Gray_03 : 'transparent')};
  color: ${({ $isSelected, theme }) => ($isSelected ? theme.colors.color_Gray_03 : theme.colors.color_Gray_05)};
  ${({ theme }) => theme.typography.Body_04};
  cursor: pointer;
`;

const ApplyButton = styled(BlackFullWidthBtn)`
  margin: 20px 0px 15px 0px;
`;
